// The rules for a guest's profile-header edits, kept free of React and
// Supabase so scripts/check-guest-profile-draft.mjs can run them under plain
// node. useUserProfile.js holds the edits while they are a guest and writes
// them at sign-in using these.

// The header fields a guest's edits are held for. Anything else a guest
// tries to save (a block's own field, like hot_takes or currently_reading) is
// refused as it always was: holding it would show it as saved, and nothing
// writes it at sign-in. avatar_url is held only as an on-screen preview and
// is never written from the draft (see buildGuestPatch).
export const HELD_FIELDS = [
  'username', 'location', 'interest_tags', 'archetype_emoji', 'archetype_label',
  'instagram_handle', 'tiktok_handle', 'twitter_handle', 'contact_email',
];
export const HOLDABLE_KEYS = new Set([...HELD_FIELDS, 'avatar_url']);

// ReaderProfile.jsx addGenre: userGenres.length >= 3
export const MAX_GENRES = 3;

// How close the account's first sign-in has to be to its creation for the
// guest's values to replace what is saved.
export const NEW_ACCOUNT_WINDOW_MS = 15 * 60 * 1000;

// Whether this sign-in is the account the guest just made. Both timestamps
// come from the auth server, so a wrong clock on the reader's phone cannot
// make an old account look new (comparing Date.now() with created_at did:
// a phone 15+ minutes slow passed the check and overwrote a saved profile).
// A first sign-in sets last_sign_in_at seconds after created_at; an existing
// account signing in has a last_sign_in_at of today and an older created_at.
// Anything missing or unreadable counts as not new, which only ever fills
// empty fields.
export function isFreshAccount(user) {
  const created = Date.parse(user?.created_at || '');
  const signedIn = Date.parse(user?.last_sign_in_at || '');
  if (!Number.isFinite(created) || !Number.isFinite(signedIn)) return false;
  const gap = signedIn - created;
  return gap >= 0 && gap < NEW_ACCOUNT_WINDOW_MS;
}

const isEmpty = v => v == null || v === '' || (Array.isArray(v) && v.length === 0);

// What to write at sign-in. A new account takes the guest's values; an
// existing one only gets fields it has no value for. Genres merge either way,
// capped at MAX_GENRES, with the new account's guest picks first. avatarUrl is
// the photo already uploaded at sign-in, never the draft's preview address.
export function buildGuestPatch(draft, saved, { isNewAccount, avatarUrl = null }) {
  const patch = {};
  for (const f of HELD_FIELDS) {
    if (!draft || !(f in draft)) continue;
    if (f === 'interest_tags') {
      const mine  = Array.isArray(draft.interest_tags) ? draft.interest_tags : [];
      const there = Array.isArray(saved?.interest_tags) ? saved.interest_tags : [];
      const merged = [...new Set(isNewAccount ? [...mine, ...there] : [...there, ...mine])].slice(0, MAX_GENRES);
      if (merged.length > 0) patch.interest_tags = merged;
    } else if (isNewAccount || isEmpty(saved?.[f])) {
      patch[f] = draft[f];
    }
  }
  if (avatarUrl && (isNewAccount || isEmpty(saved?.avatar_url))) patch.avatar_url = avatarUrl;
  return patch;
}
